import { Box, Grid } from "@mui/material";
import { useSearchParams } from "react-router-dom";
import { AggregateScoreType } from "~/operations";
import {
  ScopeTable,
  ScopeTableHeader,
  getColumns,
  useEntityScopes,
  useEntityScopesFilterContext,
} from "~/pages/platform/components";
import { organizationMrnFromId, spaceMrnFromId } from "~/hooks/useScope";
import { CheckScopeNode } from "~/pages/platform/check/types";
import { useOrganizationCheckOutletContext } from "../hooks/useOrganizationCheckOutletContext";
import { ScopeType } from "~/pages/platform/components/ScopeTable/hooks/useEntityScopes";

export function OrganizationCheckResultsTab() {
  const { findingMrn, scopeMrn } = useOrganizationCheckOutletContext();
  const [searchParams, setSearchParams] = useSearchParams();

  const organizationId = searchParams.get("organizationId");
  const spaceId = searchParams.get("spaceId");
  const viewParam = searchParams.get("view");

  const scopeType: ScopeType = viewParam
    ? (viewParam as ScopeType)
    : spaceId
      ? "Workspaces"
      : organizationId
        ? "Spaces"
        : "Organizations";

  const { filterVariables, filterForm } = useEntityScopesFilterContext({
    scopeType,
    findingMrn,
    scoreType: AggregateScoreType.Check,
  });

  const getEntityMrn = () => {
    switch (scopeType) {
      case "Organizations":
        return scopeMrn;
      case "Spaces":
        return organizationId
          ? organizationMrnFromId(organizationId)
          : scopeMrn;
      case "Workspaces":
        return spaceId ? spaceMrnFromId(spaceId) : scopeMrn;
      default:
        return scopeMrn;
    }
  };

  const {
    data: filteredData,
    loading,
    totalCount,
    paginationModel,
    onPaginationModelChange,
    rowCount,
    sortModel,
    onSortModelChange,
  } = useEntityScopes<CheckScopeNode>({
    entityMrn: getEntityMrn(),
    findingMrn,
    scopeType,
    filterVariables,
    scoreType: AggregateScoreType.Check,
  });

  const handleScopeTypeChange = (newScopeType: ScopeType) => {
    setSearchParams((prev) => {
      const newParams = new URLSearchParams(prev);
      newParams.set("view", newScopeType);
      return newParams;
    });
  };

  const handleRowClick = (rowId: string) => {
    const url = new URL(location.href);

    if (scopeType === "Organizations") {
      url.searchParams.set("organizationId", rowId);
      url.searchParams.append("organization", rowId);
      url.searchParams.delete("view");
      url.searchParams.delete("spaceId");
      url.searchParams.delete("space");
    } else if (scopeType === "Spaces") {
      url.searchParams.set("spaceId", rowId);
      url.searchParams.append("space", rowId);
      url.searchParams.delete("view");
    }

    setSearchParams(url.searchParams);
  };

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
      <Grid container spacing={2} size={12} flexDirection="column">
        <ScopeTableHeader
          scopeType={scopeType}
          totalCount={totalCount}
          onScopeTypeChange={handleScopeTypeChange}
        />

        {filterForm}

        <ScopeTable
          scopeType={scopeType}
          columns={getColumns(scopeType, "check")}
          data={filteredData}
          onScopeRowClick={handleRowClick}
          loading={loading}
          paginationModel={paginationModel}
          onPaginationModelChange={onPaginationModelChange}
          rowCount={rowCount}
          sortModel={sortModel}
          onSortModelChange={onSortModelChange}
        />
      </Grid>
    </Box>
  );
}
